import {
    eventMap,
    mergeCellPropsOnData,
    getRenderFunctionFromCellData,
    views as rowView,
} from "../../../../core/components/table/head/row";
import { componentClasses as cellClasses } from "../../../../core/components/table/head/cell";
import { eventMap as cellEvents } from "../../../../core/components/table/head/cell";
import template from "./cell.template";
import { dispatchEvent, renderState } from "../../../../core/js/state-machine";
import { types as sortType } from "../../../../core/js/data-grid/sort-by-column";
import TableHeadCell from "./cell";
import { prefix, randomNumGenerator } from "../../../../core/js/utilities";

const Head = (props) => {
    const dispose = () => {
        cells.forEach((cell, index) => {
            cell.element.removeEventListener(eventMap.sort, updateColumnSort(index));
            cell.element.removeEventListener(eventMap.pin, updatePinColumn(index));
            cell.element.removeEventListener(eventMap.headCellFocusChanged, handleCellTabStop);
            cell.element.removeEventListener(eventMap.styleUpdated, handleColumnStyleUpdated);
            cell.dispose();
        });
        cells.length = 0;
    };

    const updateColumnSort = (columnId) => {
        return (event) => {
            cells.forEach((cell) => {
                if (cell.data.cellProps.col !== columnId) {
                    cell.resetSort();
                } else {
                    const currentSort = { columnId, sortBy: event.detail };
                    api.sort = currentSort;
                    dispatchEvent(eventMap.sort, api.element, currentSort);
                }
            });
        };
    };

    const updatePinColumn = (columnId) => {
        return () => {
            const currentPinnedColumn = { columnId };
            api.pinnedColumn = currentPinnedColumn;

            dispatchEvent(eventMap.pin, api.element, currentPinnedColumn);
        };
    };

    const handleUnpinElement = () => {
        dispatchEvent(eventMap.unpin, api.element, null);
    };

    const handleCellTabStop = (event) => {
        // If the element doesn't have an action menu, do nothing.
        if (!event.target.querySelector(`.${cellClasses.actionMenu}`)) {
            return;
        }

        // Don't trigger the code if the focus trigger origin
        // is click or touch.
        if (event.detail && event.detail.type === "click") {
            return;
        }

        // When the current element is pinned, don't trigger
        if (event.target.classList.contains(cellClasses.pinned)) {
            return;
        }

        const elementIndex = cells.findIndex((cell) => cell.element === event.target);
        const width = cells.reduce((acc, item, index) => {
            return index < elementIndex ? acc + item.element.offsetWidth : acc;
        }, 0);

        dispatchEvent(eventMap.scroll, event.target, {
            cell: event.target,
            width,
        });
    };

    const handleColumnStyleUpdated = (event) => {
        dispatchEvent(eventMap.styleUpdated, element, event.detail);
    };

    /**
     * Trigger the recalculation of the cell positions
     */
    const recalculatePinnedColumn = () => {
        let widthAcc = 0;

        cells.forEach((cell, index) => {
            if (index > 0 && cell.isPinned) {
                cell.setLeftPosition(widthAcc);
                cell.updateColumnStyling();
            }
            widthAcc += cell.element.offsetWidth;
        });
    };

    /**
     * Pin the specified column
     * @param {Number} columnId Column to be pinned
     */
    const pin = (columnId) => {
        if (!pinnable) {
            return;
        }

        let widthAcc = 0;

        cells.forEach((cell, index) => {
            // Unpin all the cells that is greater than the pinned
            if (index > columnId) {
                cell.unpin();
                return;
            }

            const isPinnedColumn = columnId === index;
            cell.pin(isPinnedColumn, widthAcc);
            widthAcc += cell.element.offsetWidth;
        });
    };

    /**
     * Unpin all the columns or the specified column
     */
    const unpin = () => {
        if (!pinnable) {
            return;
        }
        cells.forEach((cell) => {
            cell.unpin();
        });
    };

    // Create a list of what is going to be rendered as column data
    const columnCells = props.data.map(mergeCellPropsOnData);
    const pinnable = props.pinnable;

    // We can only have one active sortBy at initialization
    // se need to capture the last one
    const lastSortBy = columnCells
        .filter((item) => {
            return item.sortBy === sortType.ascending || item.sortBy === sortType.descending;
        })
        .pop();

    const initialSort = lastSortBy
        ? {
              sortBy: lastSortBy.sortBy,
              columnId: lastSortBy.cellProps.col,
          }
        : null;

    // And iterate over the columns list to reset the sortBy that is not the last item
    columnCells.forEach((item, index) => {
        if (!!lastSortBy && lastSortBy.cellProps.col !== index && item.sortBy in sortType) {
            item.sortBy = sortType.unsorted;
        }
    });

    // Create a list that will provide the render function to table data
    const columnsRender = props.data.map(getRenderFunctionFromCellData);
    const element = renderState(rowView.rest, document.createElement("div"));
    const frag = document.createDocumentFragment();

    // Add an unique CSS class for the column, to be easier to modify some
    // behaviors using a single CSS class, and a last column flag.
    columnsRender.forEach((item) => {
        item.colClass = `${prefix}dynamic__col-${randomNumGenerator()}`;
    });

    /**
     * Test if the current cell can be pinned
     * @param {Number} currentIndex Index to test if the current cell can be pinned
     * @returns {Boolean}
     */
    const shouldBePinnable = (currentIndex) => {
        return currentIndex < columnCells.length - 2;
    };

    // Enable resize in another time
    // const shouldBeResizable = (resizable, resizableColumns) => {
    //     // If undefined, the default is to be enabled, because the table
    //     // can have the property to set all the columns to be resizable.
    //     const isResizable = resizable !== undefined ? resizable : true;
    //     return isResizable && resizableColumns;
    // };

    const cells = columnCells.map((data, index) => {
        const options = Object.assign(data, {
            colClass: columnsRender[index].colClass,
            pin: props.pin,
            pinnable: shouldBePinnable(index) ? pinnable : false,
            // Resizable: shouldBeResizable(data.resizable, props.resizableColumns),
        });
        const columnElement = new DOMParser().parseFromString(template(options), "text/html").body
            .firstElementChild;

        const columnAPI = TableHeadCell(columnElement, options);
        frag.appendChild(columnElement);

        if (options.sortBy) {
            columnAPI.element.addEventListener(cellEvents.sort, updateColumnSort(index));
        }

        if (options.pinnable) {
            columnAPI.element.addEventListener(cellEvents.pin, updatePinColumn(index));
            columnAPI.element.addEventListener(cellEvents.unpin, handleUnpinElement);
            columnAPI.element.addEventListener(cellEvents.headCellFocusChanged, handleCellTabStop);
        }

        // Resize handle commented until the a11y review
        // columnAPI.element.addEventListener(cellEvents.resized, recalculatePinnedColumn);
        columnAPI.element.addEventListener(cellEvents.styleUpdated, handleColumnStyleUpdated);

        return columnAPI;
    });

    element.appendChild(frag);

    const api = {
        element,
        dispose,
        pin,
        unpin,
        recalculatePinnedColumn,
        sort: initialSort,
        //Columns is used to provide to row>cell the render function and options for cell on that column
        columns: columnsRender,
        cells,
    };
    return api;
};
export default Head;
